// pages/TicketManagement/TicketManagementPage.jsx
//
// ┌─────────────────────────────────────────────────────────────────┐
// │  Mirrors SchemeManagementPage pattern exactly:                  │
// │  • useTableDataWithPost  for data fetching                      │
// │  • useSession            for userid                             │
// │  • useRolePermissions    for role-gated actions                 │
// │  • TicketFormModal       for create / edit                      │
// │  • TicketViewModal       for full detail + comments             │
// │  • DeleteModal           for delete confirmation                │
// └─────────────────────────────────────────────────────────────────┘

import React, { useState, useMemo } from "react";
import DataTable        from "components/DataTable";
import PageWrapperForm from "components/PageWrapperForm";
import DeleteModal from "components/DeleteModal";
import { useTableDataWithPost } from "hooks/useTableWithPost";
import { useSession } from "hooks/useSession";
import { useRolePermissions }   from "./components/useRolePermissions";
import { TicketManagement } from "./components/ticketManagement";
import TicketFormModal  from "./components/TicketFormModal";
import TicketViewModal  from "./components/TicketViewModal";


/* ─────────────────────────────────────────────
   Inline Stat Card  (self-contained, no deps)
───────────────────────────────────────────── */
const StatCard = ({ label, value, icon, color, bg = "#ffffff", borderColor = "#e2e8f0" }) => (
  <div style={{
    background: bg,
    border: `1px solid ${borderColor}`,
    borderRadius: "12px",
    padding: "14px 18px",
    position: "relative",
    overflow: "hidden",
    flex: "1 1 180px",
    minWidth: "140px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.03)",
    transition: "transform 0.15s ease, box-shadow 0.15s ease",
  }}>
    <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "3px", background: color }} />
    <div style={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.5px", textTransform: "uppercase", color: "#64748b", marginBottom: "6px" }}>
      {label}
    </div>
    <div style={{ fontSize: "24px", fontWeight: 700, color: "#0f172a", fontFamily: "'IBM Plex Mono', monospace", lineHeight: 1 }}>
      {value ?? "0"}
    </div>
    <div style={{ position: "absolute", right: "14px", top: "50%", transform: "translateY(-50%)", fontSize: "24px", opacity: 0.25 }}>
      {icon}
    </div>
  </div>
);

/* ─────────────────────────────────────────────
   Priority / Status inline badges for table
───────────────────────────────────────────── */
const PBadge = ({ val }) => {
  const m = TicketManagement.badgeMap.priority[val] || { color: "#475569", bg: "#f1f5f9", border: "#cbd5e1", label: val };
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: "4px",
      background: m.bg, color: m.color, border: `1px solid ${m.border || "#e2e8f0"}`,
      fontSize: "11px", fontWeight: 700,
      padding: "3px 9px", borderRadius: "20px", whiteSpace: "nowrap",
    }}>
      {m.label}
    </span>
  );
};
const SBadge = ({ val }) => {
  const m = TicketManagement.badgeMap.status[val] || { color: "#475569", bg: "#f1f5f9", border: "#cbd5e1", label: val };
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: "4px",
      background: m.bg, color: m.color, border: `1px solid ${m.border || "#e2e8f0"}`,
      fontSize: "11px", fontWeight: 700,
      padding: "3px 9px", borderRadius: "20px", whiteSpace: "nowrap",
    }}>
      {m.label}
    </span>
  );
};

/* ═══════════════════════════════════════════════
   MAIN PAGE
═══════════════════════════════════════════════ */
const TicketManagementPage = () => {
  /* ── Session & roles ── */
  const { userid }             = useSession();
  const { can, roleName }      = useRolePermissions(TicketManagement);

  /* ── Modal state (same pattern as SchemeManagementPage) ── */
  const [modalShow, setModalShow]     = useState(false);
  const [modalMode, setModalMode]     = useState("add");
  const [viewShow,  setViewShow]      = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [refresh, setRefresh]         = useState(0); // 🔄 trigger re-fetch

  /* ── Table data fetch (same pattern) ── */
  const formdata = useMemo(() => {
    const fd = new FormData();
    fd.append("action",  "GET");
    fd.append("userid",  userid);
    fd.append("role",    roleName);
    return fd;
  }, [userid, roleName, refresh]); // eslint-disable-line react-hooks/exhaustive-deps

  const tableData = useTableDataWithPost(
    TicketManagement.apiUrl,
    TicketManagement,
    formdata,
    refresh
  );

  /* ── Derive stats from tableData ── */
  const stats = useMemo(() => {
    const rows = tableData?.data || [];
    return {
      total:      rows.length,
      open:       rows.filter(r => r.status === "open").length,
      inProgress: rows.filter(r => r.status === "in_progress").length,
      critical:   rows.filter(r => r.priority === "critical" && ["open", "in_progress"].includes(r.status)).length,
      resolved:   rows.filter(r => r.status === "resolved").length,
    };
  }, [tableData]);

  /* ── Handlers (exactly mirroring SchemeManagementPage) ── */
  const handleAddTicket = () => {
    setModalMode("add");
    setSelectedTicket(null);
    setModalShow(true);
  };

  const handleView = (ticket) => {
    setSelectedTicket(ticket);
    setViewShow(true);
  };

  const handleEditTicket = (ticket) => {
    setModalMode("edit");
    setSelectedTicket(ticket);
    setModalShow(true);
  };

  const handleDelete = (ticket) => {
    setSelectedTicket(ticket);
    setTimeout(() => {
      const modalEl = document.getElementById("deleteticket1");
      if (modalEl) {
        const modal = window.bootstrap?.Modal?.getInstance(modalEl) || window.bootstrap?.Modal?.getOrCreateInstance(modalEl);
        modal?.show();
      }
    }, 100);
  };

  const handleExport = (selectedIds) => {
    if (!can.export) return;
    console.log("Exporting tickets:", selectedIds);
    alert(`Exporting ${selectedIds.length} ticket records`);
  };

  const handleDeleteSelected = (selectedIds) => {
    if (window.confirm(`Delete ${selectedIds.length} tickets?`)) {
      setRefresh(prev => prev + 1);
    }
  };

  /* ── Success callbacks (same pattern) ── */
  const handleFormSuccess = () => {
    console.log("TicketFormModal success — refreshing...");
    setRefresh(prev => prev + 1);
    setModalShow(false);
  };

  const handleDeleteSuccess = () => {
    console.log("Ticket deleted — refreshing...");
    setRefresh(prev => prev + 1);
  };

  const handleStatusChange = (newStatus) => {
    // Optimistically update the selected ticket's status
    setSelectedTicket(prev => prev ? { ...prev, status: newStatus } : prev);
    setRefresh(prev => prev + 1);
  };

  /* ── Open edit from view modal ── */
  const handleEditFromView = () => {
    setViewShow(false);
    setModalMode("edit");
    setModalShow(true);
  };

  return (
    <div style={{marginTop:"40px", padding:"20px"}}>
      {/* ── Stat Cards ── */}
      <div style={{
        display: "flex", gap: "12px", flexWrap: "wrap",
        marginBottom: "20px", fontFamily: "'DM Sans', sans-serif",
      }}>
        <StatCard label="Total Tickets" value={stats.total}      icon="🎫" color="#0d9488" bg="#f0fdfa" borderColor="#ccfbf1" />
        <StatCard label="Open"          value={stats.open}       icon="📬" color="#2563eb" bg="#eff6ff" borderColor="#bfdbfe" />
        <StatCard label="In Progress"   value={stats.inProgress} icon="⚙️"  color="#d97706" bg="#fffbeb" borderColor="#fde68a" />
        <StatCard label="Critical"      value={stats.critical}   icon="🔥" color="#dc2626" bg="#fef2f2" borderColor="#fecaca" />
        <StatCard label="Resolved"      value={stats.resolved}   icon="✅" color="#16a34a" bg="#f0fdf4" borderColor="#bbf7d0" />
      </div>

      {/* ── Role Notice ── */}
      {!can.create && (
        <div style={{
          background: "#fffbeb", border: "1px solid #fde68a",
          borderRadius: "8px", padding: "10px 16px", marginBottom: "16px",
          fontSize: "12.5px", color: "#92400e", display: "flex", alignItems: "center", gap: "8px",
        }}>
          <span>ℹ️</span>
          Your role (<strong>{roleName.replace(/_/g, " ")}</strong>) can only view tickets.
          Contact your admin to raise a ticket.
        </div>
      )}

      {/* ── Main Table (same PageWrapperForm + DataTable pattern) ── */}
      <PageWrapperForm
        config={TicketManagement}
        handleOnclick={handleAddTicket}
        buttonTitle={can.create ? "🎫 New Ticket" : null}
      >
        <DataTable
          config={TicketManagement}
          tableData={tableData}
          onView={handleView}
          onEdit={handleEditTicket}
          onDelete={handleDelete}
          onExport={can.export ? handleExport     : null}
          onDeleteSelected={handleDeleteSelected}

          /* Custom cell renderers for status / priority badges */
          cellRenderer={(col, value, row) => {
            if (col.key === "status")   return <SBadge val={value} />;
            if (col.key === "priority") return <PBadge val={value} />;
            if (col.key === "ticket_type") {
              const opt = TicketManagement.ticketTypeOptions.find(o => o.value === value);
              return <span style={{ fontSize: "12px", color: "#8892a4" }}>{opt?.label || value}</span>;
            }
            if (col.key === "amount") {
              return value
                ? <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "12px", color: "#00d4aa" }}>
                    ₹ {Number(value).toLocaleString("en-IN")}
                  </span>
                : <span style={{ color: "#525d72", fontSize: "12px" }}>—</span>;
            }
            if (col.key === "assigned_to") {
              return row.assigned_to_name
                ? <span style={{ fontSize: "12.5px", color: "#e8eaf0" }}>{row.assigned_to_name}</span>
                : <span style={{ fontSize: "12px", color: "#525d72" }}>Unassigned</span>;
            }
            return value ?? "—";
          }}
        />
      </PageWrapperForm>

      {/* ── Create / Edit Modal ── */}
      <TicketFormModal
        show={modalShow}
        onClose={() => setModalShow(false)}
        mode={modalMode}
        userData={selectedTicket}
        onSuccess={handleFormSuccess}
      />

      {/* ── View Detail Modal ── */}
      <TicketViewModal
        show={viewShow}
        onClose={() => setViewShow(false)}
        ticket={selectedTicket}
        onStatusChange={handleStatusChange}
        onEditOpen={handleEditFromView}
      />

      {/* ── Delete Confirmation Modal (reusing your DeleteModal) ── */}
      <DeleteModal
        deleteid={selectedTicket?.id || selectedTicket?.ticket_id}
        id="deleteticket1"
        title="Delete Ticket"
        message={`Are you sure you want to delete ticket ${selectedTicket?.ticket_id || selectedTicket?.title || ""}? This action cannot be undone.`}
        apiUrl={TicketManagement.apiUrl}
        onSuccess={handleDeleteSuccess}
      />
    </div>
  );
};

export default TicketManagementPage;